<script setup lang="ts">
import {ref} from "vue";
import {useStorage} from "@vueuse/core";
import {useRoute, useRouter} from "vue-router";
import {getUser, storeUser, updateUser} from "@/Services/Admin/admin-users";
import {getPlans} from "@/Services/Admin/admin-plans";
import {saving} from "@/Composables/api";
import Countries from "@/Data/countries-array";
import {Button, Card, ColorPicker, FileUpload, InputText, Label, Password, Select} from "@/Components/Library";
import {PageHeading} from "@/Components/Global";
import {FormButtons} from "@/Components/Forms";
import {deleteAvatar as apiDeleteAvatar} from "@/Services/users";
import {useMainStore} from "@/Stores/store";
import {useToast} from "primevue/usetoast";
import {Colors} from "@/Enums";

const $store = useMainStore();
const toast = useToast();
const currentRoute = useRoute();
const router = useRouter();

const defaultForm = {
    id: null,
    first_name: null,
    last_name: null,
    nickname: null,
    email: null,
    password: null,
    password_confirmation: null,
    plan_id: null,
    profile: {
        id: null,
        avatar: null,
        color: Colors.random(),
        country: null,
    },
};
const avatarUploadRef = ref<FileUpload>();
const plans = ref([]);
getPlans().then(({data}) => (plans.value = data));

const user = useStorage("admin-user-form", defaultForm);

if (currentRoute.params.user) {
    user.value = defaultForm;
    getUser(currentRoute.params.user).then(({data}) => {
        Object.assign(user.value, data);
        user.value.profile.color = data.profile.color.slug;
    });
} else if (user.value.id) {
    user.value = defaultForm;
}

const save = async () => {
    try {
        if (currentRoute.params.user) {
            await updateUser(user.value);
            avatarUploadRef.value?.startUpload();
        } else {
            console.log("saving user");
            await storeUser(user.value).then(({data}) => {
                console.log("user saved", data);
                Object.assign(user.value, data);
                router.push({name: "admin.users.edit", params: {user: data.id}});
            });
        }

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "User saved successfully",
            life: 3000,
        });
    } catch (error) {
        console.error(error);
    } finally {
        user.value.password = null;
        user.value.password_confirmation = null;
    }
};

const deleteAvatar = async () => {
    apiDeleteAvatar(user.value.profile.id, user.value.profile.avatar.id).then(() => {
        user.value.profile.avatar = null;
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Avatar deleted successfully",
            life: 3000,
        });
    });
};

const handleAvatarUpload = async () => {
    const {data} = await getUser(user.value.id);
    user.value.profile.avatar = data.profile.avatar;
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-12">
        <div class="m-container mb-3">
            <PageHeading :title="`${currentRoute.params.user ? 'Edit' : 'Create'} User`" />
        </div>
        <form class="flex flex-col gap-4" @submit.prevent="save">
            <Card>
                <template #title>Details</template>
                <div class="flex flex-col gap-4">
                    <div class="grid-cols-2 gap-6 md:grid">
                        <div class="">
                            <Label for="first_name" class="mb-1" required>First name</Label>
                            <InputText
                                id="first_name"
                                name="first_name"
                                placeholder="First name"
                                v-model="user.first_name"
                                required
                                fluid
                            />
                        </div>
                        <div>
                            <Label for="last_name" class="mb-1" required>Last name</Label>
                            <InputText
                                id="last_name"
                                name="last_name"
                                placeholder="Last name"
                                v-model="user.last_name"
                                required
                                fluid
                            />
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <Label for="nickname" class="mb-1">Nickname</Label>
                        <InputText
                            id="nickname"
                            name="nickname"
                            type="text"
                            placeholder="Nickname"
                            v-model="user.nickname"
                            fluid
                        />
                    </div>
                    <div>
                        <Label for="email" class="mb-1" required>Email</Label>
                        <InputText
                            id="email"
                            name="email"
                            placeholder="Email address"
                            v-model="user.email"
                            required
                            fluid
                        />
                    </div>
                    <div class="flex flex-col">
                        <Label for="country" class="mb-1">Country</Label>
                        <Select
                            input-id="country"
                            name="country"
                            placeholder="Select a country"
                            v-model="user.profile.country"
                            fluid
                            :options="Countries"
                        />
                    </div>
                </div>
            </Card>
            <Card>
                <template #title>Profile</template>
                <div class="flex flex-col gap-4">
                    <div>
                        <Label for="color" class="mb-1">Color</Label>
                        <ColorPicker id="color" name="color" v-model="user.profile.color" />
                    </div>
                </div>
            </Card>
            <Card>
                <template #title>Avatar</template>
                <template #content>
                    <div class="flex flex-col">
                        <div
                            v-if="user?.profile?.avatar"
                            :key="user?.profile?.avatar.id"
                            class="relative mx-auto mb-3 aspect-square max-h-48 max-w-48"
                        >
                            <Button
                                class="!absolute right-0 top-0 z-20 !p-1 text-red-600 hover:bg-red-600 hover:text-white"
                                :icon="{
                                    icon: 'mdi:close',
                                    width: '20',
                                }"
                                @click.stop="deleteAvatar"
                            />
                            <div class="h-full w-full overflow-hidden rounded-full">
                                <img
                                    :src="user?.profile?.avatar?.url"
                                    :alt="user.first_name"
                                    class="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                        <FileUpload
                            ref="avatarUploadRef"
                            id="avatar_image"
                            name="avatar_image"
                            collection="avatars"
                            :parent-id="user.profile?.id"
                            parent-type="user-profile"
                            accept="image/*"
                            @upload="handleAvatarUpload"
                        />
                    </div>
                </template>
            </Card>
            <Card>
                <template #title>Plan</template>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-col">
                        <Label for="plan_id" class="mb-1">Plan</Label>
                        <Select
                            input-id="plan_id"
                            name="plan_id"
                            placeholder="Select a plan"
                            v-model="user.plan_id"
                            fluid
                            :options="plans"
                            option-label="name"
                            option-value="id"
                        />
                    </div>
                </div>
            </Card>
            <Card>
                <template #title>Password</template>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-col">
                        <Label for="password" class="mb-1">Password</Label>
                        <Password
                            inputId="password"
                            name="password"
                            v-model="user.password"
                            placeholder="Leave empty to keep current password"
                            fluid
                            toggleMask
                            :feedback="false"
                        />
                    </div>
                    <div class="flex flex-col">
                        <Label for="password_confirmation" class="mb-1">Confirm Password</Label>
                        <Password
                            inputId="password_confirmation"
                            name="password_confirmation"
                            placeholder="Confirm password"
                            v-model="user.password_confirmation"
                            fluid
                            toggleMask
                            :feedback="false"
                        />
                    </div>
                </div>
            </Card>
            <FormButtons :show-cancel="false" :loading="!!saving" />
        </form>
    </div>
</template>
<style scoped></style>
